import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  CalendarDays,
  ChevronRight,
  Eye,
  Search,
  ShieldCheck,
  X,
} from "lucide-react";
import { AppDispatch, RootState } from "../../../../store";
import { getUnityCommission } from "../../../../Redux/referral/slice";
import Pagenation from "../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import {
  formatEarningsCurrency,
  formatEarningsDate,
  formatEarningsLongDate,
  normalizeEarningsStatus,
  statusLabel,
} from "../earningsAdapters";
import "./SalarUnityIncentives.css";

const statusClass = (status: string) =>
  `salar-unity-status salar-unity-status-${normalizeEarningsStatus(status)}`;

const SalarUnity = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { unityCommission } = useSelector(
    (state: RootState) => state.referral,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (dateRange.from && !dateRange.to) return;

    dispatch(
      getUnityCommission({
        page,
        limit,
        status: "ALL",
        sortOrder: "DESC",
        search: debouncedSearch || undefined,
        startDate: dateRange.from || undefined,
        endDate: dateRange.to || undefined,
      }),
    );
  }, [dispatch, page, limit, debouncedSearch, dateRange.from, dateRange.to]);

  const records = unityCommission.data || [];
  const totalRecords =
    unityCommission.pagination?.total ?? records.length;

  const itemStatus = (item: (typeof records)[number]) =>
    item.receivedStatus
      ? "Success"
      : item.pendingStatus
        ? "Pending"
        : item.cancelledStatus
          ? "Cancelled"
          : item.status;

  return (
    <section className="salar-unity-page">
      <div className="salar-unity-shell">
        <header className="salar-unity-page-header">
          <div className="salar-unity-title-wrap">
            <span className="salar-unity-title-icon">
              <ShieldCheck size={23} />
            </span>
            <div>
              <span className="salar-unity-eyebrow">
                REFERRAL INCENTIVES
              </span>
              <h1>Salar Unity Referral Incentives</h1>
              <p>
                Track Salar Unity milestones, SUR rewards, bonus incentives
                and purchased voucher details.
              </p>
            </div>
          </div>
          <span className="salar-unity-count-pill">
            {totalRecords} records
          </span>
        </header>

        <div className="salar-unity-toolbar">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Entries per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option value={val} key={val}>
                {val} Entries
              </option>
            ))}
          </select>

          <div className="salar-unity-date-picker">
            <DateRangePicker
              value={dateRange}
              onChange={(v) => {
                setDateRange(v);
                setPage(1);
              }}
            />
          </div>

          <span className="salar-unity-toolbar-count">
            {records.length} incentives
          </span>

          <div className="salar-unity-search">
            <Search size={17} />
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search incentives..."
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        <div className="salar-unity-desktop-list">
          {records.map((item, index) => (
            <article className="salar-unity-desktop-card" key={index}>
              <div className="salar-unity-meta-row">
                <div>
                  <span>Achieved Date</span>
                  <strong>{formatEarningsDate(item.achievedDate)}</strong>
                </div>
                <div>
                  <span>Partner Network ID</span>
                  <strong>{item.partnerNetworkId}</strong>
                </div>
                <div>
                  <span>Achievement Details</span>
                  <strong>{item.achievementDetails?.milestone}</strong>
                </div>
                <div className="salar-unity-status-cell">
                  <span>Status</span>
                  <span className={statusClass(itemStatus(item))}>
                    {statusLabel(itemStatus(item))}
                  </span>
                </div>
              </div>

              <div className="salar-unity-record-body">
                <div className="salar-unity-purchased-line">
                  <div>
                    <strong>
                      Purchased On {formatEarningsLongDate(item.date)}
                    </strong>
                    <span>{item.productName}</span>
                  </div>
                  <Link
                    className="salar-unity-view-details"
                    to="/SalarUnityDetails"
                  >
                    <Eye size={16} />
                    View Details
                  </Link>
                </div>

                <div className="salar-unity-product-row">
                  <img
                    src={item.productImage || "../../assets/img/mobiles/mobiles-4.png"}
                    alt=""
                    className="salar-unity-product-image"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "../../assets/img/mobiles/mobiles-4.png";
                    }}
                  />
                  <div className="salar-unity-product-copy">
                    <span className="salar-unity-section-label">
                      SALAR UNITY PURCHASE
                    </span>
                    <h2>{item.productName}</h2>
                    <p>{item.achievementDetails?.milestone}</p>
                    <div className="salar-unity-code-row">
                      Salar Code: <strong>{item.salarCode}</strong>
                    </div>
                  </div>
                </div>

                <div className="salar-unity-info-grid">
                  <div>
                    <span>Salar Shopping Voucher</span>
                    <strong>
                      {formatEarningsCurrency(item.productPrice)}
                    </strong>
                  </div>
                  <div>
                    <span>Received Incentives</span>
                    <strong>
                      {formatEarningsCurrency(
                        item.grossAmount ??
                          item.receivedIncentives?.receivedSURIncentive,
                      )}
                    </strong>
                    <small>Received SUR Incentive</small>
                  </div>
                  <div>
                    <span>TDS</span>
                    <strong>
                      {formatEarningsCurrency(item.tdsAmount ?? 0)}
                      {item.tdsIsEstimated ? " (est)" : ""}
                    </strong>
                    <small>
                      {item.tdsPercentage != null
                        ? `${item.tdsPercentage}%`
                        : ""}
                    </small>
                  </div>
                  <div>
                    <span>Net Credited</span>
                    <strong>
                      {formatEarningsCurrency(
                        item.netAmount ?? item.receivedUnityCommission,
                      )}
                    </strong>
                    <small>After TDS</small>
                  </div>
                  <div>
                    <span>Bonus SUR Incentive</span>
                    <strong>
                      {formatEarningsCurrency(
                        item.bonusSURIncentive?.receivedBonusSURIncentive ?? 0,
                      )}
                    </strong>
                    <small>{item.bonusSURIncentive?.milestone}</small>
                  </div>
                </div>

                {item.bonusSURIncentive && (
                  <div className="salar-unity-target-strip">
                    <div>
                      <span>Target Days</span>
                      <strong>{item.bonusSURIncentive.targetDays} Days</strong>
                    </div>
                    <div>
                      <span>Target Achieved Days</span>
                      <strong>
                        {item.bonusSURIncentive.targetAchievedDays} Days
                      </strong>
                    </div>
                    <div>
                      <span>Milestone</span>
                      <strong>{item.bonusSURIncentive.milestone}</strong>
                    </div>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>

        <div className="salar-unity-mobile-list">
          {records.map((item, index) => (
            <Link
              className="salar-unity-mobile-card"
              key={index}
              to="/SalarUnityDetails"
            >
              <span className="salar-unity-mobile-index">{item.sNo}</span>
              <img
                src={item.productImage || "../../assets/img/mobiles/mobiles-4.png"}
                alt=""
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "../../assets/img/mobiles/mobiles-4.png";
                }}
              />
              <span className="salar-unity-mobile-copy">
                <span className="salar-unity-mobile-topline">
                  <span className={statusClass(itemStatus(item))}>
                    {statusLabel(itemStatus(item))}
                  </span>
                  <span className="salar-unity-mobile-date">
                    <CalendarDays size={14} />
                    {formatEarningsDate(item.date)}
                  </span>
                </span>
                <strong className="salar-unity-mobile-title">
                  {item.productName}
                </strong>
                <span className="salar-unity-mobile-description">
                  {item.achievementDetails?.milestone}
                </span>
                <span className="salar-unity-mobile-bottomline">
                  <strong>
                    {formatEarningsCurrency(
                      item.netAmount ?? item.receivedUnityCommission,
                    )}
                  </strong>
                  <span>
                    {item.partnerNetworkId}
                    {item.tdsAmount != null &&
                      ` · TDS ${formatEarningsCurrency(item.tdsAmount)}`}
                  </span>
                </span>
              </span>
              <ChevronRight
                size={18}
                className="salar-unity-mobile-chevron"
              />
            </Link>
          ))}
        </div>

        {records.length === 0 && (
          <div className="salar-unity-empty">
            <ShieldCheck size={27} strokeWidth={1.7} />
            <strong>No Salar Unity incentives found</strong>
            <span>Change the search or selected date range.</span>
          </div>
        )}

        <Pagenation
          currentPage={page}
          totalPages={unityCommission.pagination?.totalPages || 0}
          onPageChange={setPage}
        />
      </div>
    </section>
  );
};

export default SalarUnity;
